import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { useMediaQuery } from "@mui/material";
import Button from "app/components/UI/Button";
import PageLoading from "app/components/CommonPage/PageLoading";
import PageError from "app/components/CommonPage/PageError";
import InputText from "app/components/UI/Form/Input/InputText";
import {
  invoiceServices,
  quotationServices,
  commonServices,
} from "app/services/api";
import {
  callApiStatus,
  fileNameType,
  LABEL_WIDTH,
} from "app/constants";
import PageLoadingBackdrop from "../../../components/CommonPage/PageLoadingBackdrop";
import SelectCustomerPic from "../../../components/UI/Form/Select/SelectCustomerPic";
import messages from "../../../services/api/messages";
import InputDate from "../../../components/UI/Form/Input/InputDate";
import InvoiceItemInfosTable from "./InvoiceItemInfosTable";

const UpdateInvoiceForm = ({
  initUpdateInvoice,
  customerId,
  scheduleId,
  caseInfo,
  customerName,
}) => {
  const isMd = useMediaQuery("(max-width:768px)");

  const [updateInvoice, setUpdateInvoice] =
    useState(initUpdateInvoice);
  const [quotationInfo, setQuotationInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const loadCustomerInfo = useCallback(() => {
    setQuotationInfo(null);
    if (caseInfo.quotation) {
      quotationServices
        .getQuotationById(caseInfo.quotation)
        .then((res) => {
          setQuotationInfo(res.data);
        })
        .catch((error) => {
          console.log("error: ", error);
          toast.error(messages.loadQuotationFail);
        });
    }
  }, [caseInfo.quotation]);

  useEffect(() => {
    loadCustomerInfo();
  }, [loadCustomerInfo]);

  const getFormData = (rawData) => {
    return {
      caseInfoId: caseInfo._id,
      customerId: customerId,
      testScheduleId: scheduleId,
      personInCharge: rawData.customerPic,
      memo: rawData.memo,
      paymentDeadline: rawData.paymentDeadline,
      invoiceItemInfos: Array.isArray(rawData.invoiceItemInfos)
        ? rawData.invoiceItemInfos.map((item) => ({
            name: item.name,
            contractType: item.contractType,
            volumn: item.volumn,
            testPeopleNumber: item.testPeopleNumber,
            testSampleNumber: item.testSampleNumber,
            unitPrice: item.unitPrice,
            totalPrice:
              Number(item.unitPrice) * Number(item.testSampleNumber),
          }))
        : [],
    };
  };

  const isDisabledSubmit = useMemo(() => {
    return (
      !updateInvoice?.generatedInvoiceExcel ||
      !updateInvoice?.generatedDeliverySlipExcel
    );
  }, [
    updateInvoice?.generatedDeliverySlipExcel,
    updateInvoice?.generatedInvoiceExcel,
  ]);

  const handleChangeUpdateInvoiceInfo = (field, value) => {
    setUpdateInvoice({
      ...updateInvoice,
      [field]: value,
      generatedInvoicePdf: "",
      generatedInvoiceExcel: "",
      generatedDeliverySlipPdf: "",
      generatedDeliverySlipExcel: "",
    });
  };

  const handleConfirmUpdate = () => {
    if (!updateInvoice.paymentDeadline) {
      toast.error(messages.required("振込期限"));
      return;
    }

    const data = getFormData(updateInvoice);

    console.log("DATA:", data);
    invoiceServices
      .createFileDownload(data)
      .then((res) => {
        setUpdateInvoice((oldState) => ({
          ...oldState,
          generatedInvoiceExcel: res.data.fileBill._id,
          generatedDeliverySlipExcel: res.data.fileDeliverly._id,
        }));
        const caseId = caseInfo.caseId || "";
        const invoiceFileType = fileNameType["invoice"];
        const deliveryFileType = fileNameType["delivery"];
        const timeStamp = moment().format("YYYYMMDDHHmmss");

        setTimeout(() => {
          commonServices
            .downloadWithFileId(
              res.data.fileBill._id,
              "xlsx",
              `${invoiceFileType}_${caseId}_${customerName}_${timeStamp}`
            )
            .catch(() => toast.error(messages.downloadFail));
        }, 1000);

        setTimeout(() => {
          commonServices
            .downloadPDFWithFileId(
              res.data.fileBill._id,
              `${invoiceFileType}_${caseId}_${customerName}_${timeStamp}`
            )
            .catch(() => toast.error(messages.downloadFail));
        }, 1000);

        setTimeout(() => {
          commonServices
            .downloadWithFileId(
              res.data.fileDeliverly._id,
              "xlsx",
              `${deliveryFileType}_${caseId}_${customerName}_${timeStamp}`
            )
            .catch(() => toast.error(messages.downloadFail));
        }, 1500);

        setTimeout(() => {
          commonServices
            .downloadPDFWithFileId(
              res.data.fileDeliverly._id,
              `${deliveryFileType}_${caseId}_${customerName}_${timeStamp}`
            )
            .catch(() => toast.error(messages.downloadFail));
        }, 1500);

        toast.success(messages.confirm(true));
        setIsLoading(false);
      })
      .catch((error) => {
        toast.error(messages.confirm(false));
        setIsLoading(false);
      });
  };

  const handleSubmitUpdate = () => {
    if (isDisabledSubmit) return;
    if (!updateInvoice.paymentDeadline) {
      toast.error(messages.required("振込期限"));
      return;
    }

    const data = getFormData(updateInvoice);
    invoiceServices
      .createInvoice({
        ...data,
        generatedInvoiceExcel: updateInvoice.generatedInvoiceExcel,
        generatedDeliverySlipExcel:
          updateInvoice.generatedDeliverySlipExcel,
        isUpdate: true,
      })
      .then(() => {
        toast.success(messages.update(true));
        setIsLoading(false);
      })
      .catch(() => {
        toast.error(messages.update(false));
        setIsLoading(false);
      });
  };

  return (
    <>
      <div className="w-full h-auto">
        <div className="w-full h-auto flex items-center justify-center mb-3 flex-wrap">
          <div className="w-full mb-3 md:mb-0 md:w-1/2 h-full md:pr-12">
            <InputText
              id="quotationId"
              value={quotationInfo?.quotationId || ""}
              label="見積番号"
              disabled={true}
              labelWidth={isMd ? LABEL_WIDTH.RIGHT : LABEL_WIDTH.LEFT}
            />
          </div>

          <div className="w-full md:w-1/2 h-full">
            <SelectCustomerPic
              pic={{ id: updateInvoice?.customerPic }}
              setPic={(newPic) => {
                handleChangeUpdateInvoiceInfo(
                  "customerPic",
                  newPic?.id
                );
              }}
              labelWidth={LABEL_WIDTH.RIGHT}
              disabled={true}
            />
          </div>
        </div>

        <div className="w-full h-auto flex items-center justify-center mb-3 flex-wrap">
          <div className="w-full md:w-1/2 h-full md:pr-12">
            <InputDate
              id="paymentDeadline"
              value={updateInvoice.paymentDeadline}
              onChange={(newValue) => {
                handleChangeUpdateInvoiceInfo(
                  "paymentDeadline",
                  newValue
                );
              }}
              label="振込期限"
              labelWidth={isMd ? LABEL_WIDTH.RIGHT : LABEL_WIDTH.LEFT}
              isRequired
            />
          </div>
          <div className="w-1/2 h-full"></div>
        </div>

        <div className="w-full h-auto flex items-center justify-center mb-8">
          <InputText
            value={updateInvoice.memo}
            onChange={(newValue) => {
              handleChangeUpdateInvoiceInfo("memo", newValue);
            }}
            label="備考"
            labelWidth={isMd ? LABEL_WIDTH.RIGHT : LABEL_WIDTH.LEFT}
          />
        </div>

        <div className="mb-[14px] w-full overflow-x-auto">
          <InvoiceItemInfosTable
            invoiceItemInfos={updateInvoice.invoiceItemInfos}
            setInvoiceItemInfos={(newValue) => {
              handleChangeUpdateInvoiceInfo(
                "invoiceItemInfos",
                newValue
              );
            }}
          />
        </div>

        <div className="w-full h-auto flex items-center justify-end gap-3">
          <Button onClick={handleConfirmUpdate}>確認する</Button>

          <Button
            disabled={isDisabledSubmit}
            onClick={handleSubmitUpdate}
            type={isDisabledSubmit ? "disabled" : "primary"}
          >
            修正する
          </Button>
        </div>
      </div>

      <PageLoadingBackdrop open={isLoading} />
    </>
  );
};

const UpdateInvoice = ({ caseInfo, customer, schedule }) => {
  const [loadInitState, setLoadInitState] = useState({
    loadingStatus: callApiStatus.idle,
    data: null,
    error: null,
  });

  const loadInitInvoice = useCallback(() => {
    if (!caseInfo._id) return;

    setLoadInitState({
      loadingStatus: callApiStatus.loading,
      data: null,
      error: null,
    });

    invoiceServices
      .getById(caseInfo.invoice)
      .then((res) => {
        if (res.data.invoiceItemInfos.length === 0) {
          invoiceServices
            .getInitNewInvoice(customer.id, caseInfo.quotation)
            .then((initInvoice) => {
              setLoadInitState({
                loadingStatus: callApiStatus.success,
                data: initInvoice,
                error: null,
              });
            })
            .catch((error) => {
              toast.error(messages.callApiFail);
              setLoadInitState({
                loadingStatus: callApiStatus.error,
                data: null,
                error: error,
              });
            });
        } else {
          setLoadInitState({
            loadingStatus: callApiStatus.success,
            data: res.data,
            error: null,
          });
        }
      })
      .catch((error) => {
        toast.error(messages.callApiFail);
        setLoadInitState({
          loadingStatus: callApiStatus.error,
          data: null,
          error: error,
        });
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseInfo]);

  useEffect(() => {
    loadInitInvoice();
  }, [loadInitInvoice]);

  if (loadInitState.loadingStatus === callApiStatus.loading) {
    return <PageLoading />;
  }

  if (
    loadInitState.loadingStatus === callApiStatus.error ||
    loadInitState.error
  ) {
    return <PageError />;
  }

  if (
    loadInitState.loadingStatus === callApiStatus.success &&
    loadInitState.data
  ) {
    return (
      <UpdateInvoiceForm
        initUpdateInvoice={loadInitState.data}
        customerId={customer?.id}
        scheduleId={schedule?.id}
        caseInfo={caseInfo}
        customerName={customer?.companyNameKanji || ""}
      />
    );
  }

  return null;
};

export default UpdateInvoice;
